import { DealsTable } from '../../../components/Tables/DealsTable/DealsTable'
import React from 'react'
import { formatInteger } from '../../../utils/formatting'
import { DEAL_TABLE_KEY } from 'constants/tableQueryKeys'
import useApiService from 'services/api'
import {
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { FBPage } from '../../../components/FbUI/Page/FBPage'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { MetricCard } from 'components/FbUI/MetricCard'
import { PiHandshake, PiTable, PiKanban, PiGearSix } from 'react-icons/pi'
import { PillTabs, TabPanel } from 'components/FbUI/PillTabs'
import { DealsKanbanBoard } from 'components/Deals/DealsKanbanBoard'
import { ToolbarDensityProvider } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import { Link } from 'react-router-dom'
import useCachedState from 'utils/useCachedState'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterIdentifier } from 'models/saved_view'

type DealsView = 'table' | 'board'

/**
 * Build the params for the header "Total Deals" count query.
 *
 * The count and the table both read `reports/deals/`; threading the table's
 * active filters here keeps the metric in sync with the filtered view instead
 * of showing the unfiltered grand total. `page_size: 1` keeps the fetch tiny —
 * we only read `.count` off the paginated response.
 */
export function buildReportDealsCountParams(
  filterParams: Record<string, unknown>
): Record<string, unknown> {
  return { ...filterParams, page_size: 1 }
}

/** Persist the rep's Table/Board choice across navigation (e.g. opening a deal
 *  and coming back) and reloads. */
const DEALS_VIEW_STORAGE_KEY = 'deals-view-mode'
const isDealsView = (v: unknown): v is DealsView =>
  v === 'table' || v === 'board'

const DEALS_VIEW_TABS: ReadonlyArray<{
  key: DealsView
  title: React.ReactNode
}> = [
  {
    key: 'table',
    title: (
      <span className="flex items-center gap-1.5">
        <PiTable size={16} />
        Table
      </span>
    ),
  },
  {
    key: 'board',
    title: (
      <span className="flex items-center gap-1.5">
        <PiKanban size={16} />
        Board
      </span>
    ),
  },
]

export function DealsPage() {
  const [view, setView] = useCachedState<DealsView>(
    'table',
    DEALS_VIEW_STORAGE_KEY,
    isDealsView
  )
  // `dealDetailPage` gates the pre-existing Deals Table V2 column set;
  // `dealKanbanView` independently gates this PR's Table/Board toggle + Kanban
  // board, so the board can roll out separately from the V2 table audience.
  const { dealDetailPage, dealKanbanView } = useFeatureFlagService()
  const api = useApiService()

  // Header metric reflects the deals currently in view: it reads the same
  // report-deals read-model and the same shared filter store the table uses
  // ('DEALS_TABLE' — see DealsTableComponent[V2]), so applied filters update
  // the count too. A tiny page_size:1 fetch; we only read `.count`.
  const dealsFilterStore = filterStoreRepo.getStore(
    'DEALS_TABLE' as FilterIdentifier
  )
  const dealsFilterParams = useFilterParams(dealsFilterStore)
  const { data: totalResp } = api.useGetReportDeals(
    buildReportDealsCountParams(dealsFilterParams)
  )
  const totalDeals = totalResp?.count
  const dealsMetric = totalDeals == null ? '—' : formatInteger(totalDeals)

  const defaultColumnVisibility = dealDetailPage
    ? {
        deal: true,
        identifier: false,
        deal_name: false,
        predicted_annual_revenue: false,
        predicted_monthly_revenue: false,
        monthly_revenue_override: false,
      }
    : {
        identifier: false,
        predicted_annual_revenue: false,
        predicted_monthly_revenue: false,
        monthly_revenue_override: false,
      }

  const tableContent = (
    // sm toolbar density (search + Filter/Columns) for the deals table; the
    // provider sits above DataTable's createPortal so the Columns button is
    // covered too.
    <ToolbarDensityProvider value="sm">
      <DealsTable
        useReports={true}
        tableKey={DEAL_TABLE_KEY}
        defaultColumnVisibility={defaultColumnVisibility}
        showCompanyColumn
      />
    </ToolbarDensityProvider>
  )

  return (
    <FBPage
      // Tighter outer padding than the global FBPage default so the content
      // uses more of the viewport and the table/board get more height. Matches
      // the campaigns page; scoped here via the className overrides (global
      // --page-*-padding vars untouched, so other pages keep standard padding).
      innerPageClassName="!bg-surface-page !p-[8px]"
      containerClassName="!bg-surface-page !shadow-none !px-[16px] !py-[12px]"
    >
      {/* +8px over the default 12px header margin so this tighter page gets a
          little more breathing room below the header (matches Targets). */}
      <FBPageHeader className="!mb-[20px]">
        <FBPageTitleRow>
          <div className="flex flex-col">
            {/* Eyebrow badge signals the section (mirrors the Contacts page),
                freeing the title below to describe what this view actually
                does now that there's a board alongside the table. */}
            <span className="mb-2 inline-flex items-center gap-1.5 self-start rounded-full bg-neutral-100 px-2.5 py-0.5 text-sm font-medium tracking-tight text-neutral-700">
              <PiHandshake size={14} />
              Deals
            </span>
            <FBPageTitle className="!text-fg">Sales pipeline</FBPageTitle>
          </div>
          <div className="ml-auto">
            <MetricCard
              className="border-0"
              icon={<PiHandshake size={18} />}
              label="Total Deals"
              value={dealsMetric}
            />
          </div>
        </FBPageTitleRow>
      </FBPageHeader>
      {dealKanbanView && (
        <div className="mb-2 flex items-center gap-3">
          <PillTabs
            tabs={DEALS_VIEW_TABS}
            activeKey={view}
            onChange={setView}
            idPrefix="deals-view"
            aria-label="Deals view"
            size="sm"
            className="w-fit"
          />
          {/* DS2026 bright-green text link, aligned with the view tabs, into the
              Sales Stages settings table — the source of truth for the stages
              that become this board's columns. */}
          <Link
            to="/settings/platform/sales-stages"
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-brand-active transition-colors hover:text-basil-800"
          >
            <PiGearSix size={16} />
            Stage Settings
          </Link>
        </div>
      )}
      {dealKanbanView ? (
        <>
          <TabPanel<DealsView>
            idPrefix="deals-view"
            tabKey="table"
            activeKey={view}
            className="flex min-h-0 flex-1 flex-col"
          >
            <TableLinkStyleProvider variant="plain">
              <FBPageTableContainer className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
                {tableContent}
              </FBPageTableContainer>
            </TableLinkStyleProvider>
          </TabPanel>
          <TabPanel<DealsView>
            idPrefix="deals-view"
            tabKey="board"
            activeKey={view}
            className="flex min-h-0 flex-1 flex-col"
          >
            <ToolbarDensityProvider value="sm">
              <DealsKanbanBoard />
            </ToolbarDensityProvider>
          </TabPanel>
        </>
      ) : (
        <TableLinkStyleProvider variant="plain">
          <FBPageTableContainer className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
            {tableContent}
          </FBPageTableContainer>
        </TableLinkStyleProvider>
      )}
    </FBPage>
  )
}
